import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions, type AppModule } from "@/hooks/usePermissions";
import { SplashScreen } from "@/components/SplashScreen";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { useReviewAccount } from "@/hooks/useReviewAccount";
import type { AppRole } from "@/types/auth";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: Exclude<AppRole, "commercial">;
  requiredModule?: AppModule;
}

export function ProtectedRoute({ children, requiredRole, requiredModule }: ProtectedRouteProps) {
  const { user, role, loading, signOut } = useAuth();
  const {
    hasPermission,
    isLoading: permissionsLoading,
    isError: permissionsError,
    refetch,
  } = usePermissions();
  const location = useLocation();
  const review = useReviewAccount();

  if (loading || permissionsLoading) {
    return <SplashScreen message="Carregando seu painel" />;
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Wait for the server classification before mounting any production screen
  if (review.isLoading) {
    return <SplashScreen message="Carregando seu painel" />;
  }
  if (review.isError) {
    return <AccessDenied permissionCheckFailed onRetry={async () => review.retry()} onExit={signOut} />;
  }
  // Isolated App Review account never enters production screens
  if (review.isReview) {
    return <Navigate to="/revisao" replace />;
  }

  const denyAccess = (permissionCheckFailed = false) => (
    <AccessDenied
      permissionCheckFailed={permissionCheckFailed}
      onRetry={refetch}
      onExit={signOut}
    />
  );

  // Check module-based permission (takes precedence if specified)
  if (requiredModule) {
    if (permissionsError) {
      return denyAccess(true);
    }

    if (!hasPermission(requiredModule, 'view')) {
      return denyAccess();
    }
  }

  // Check role-based access (fallback for legacy routes)
  if (requiredRole && !requiredModule) {
    const roleHierarchy: Record<Exclude<AppRole, "commercial">, number> = {
      admin: 3,
      professional: 2,
      receptionist: 1,
    };
    const userLevel = role && Object.prototype.hasOwnProperty.call(roleHierarchy, role)
      ? roleHierarchy[role as Exclude<AppRole, "commercial">]
      : Number.NEGATIVE_INFINITY;
    const requiredLevel = roleHierarchy[requiredRole];

    if (userLevel < requiredLevel) {
      return denyAccess();
    }
  }

  return <>{children}</>;
}
